// src/utils/gis/attributeAnalyzer.js
// Inspects layer data to discover which attributes are usable for legend
// symbology, purely by the *shape* of the values — not by field name.

const DEFAULT_IGNORE_FIELDS = [
  "geom", "geometry", "id", "fid", "gid", "__gid", "uid", "scuid", "cuid",
  "path", "photo", "meter_imag", "longitude", "latitude", "x", "y",
  "accuracy", "completion", "featureId", "layerName", "n",
];

const MAX_CATEGORIES = 20;

function shouldIgnoreField(field, extraIgnoreFields = []) {
  return [...DEFAULT_IGNORE_FIELDS, ...extraIgnoreFields].includes(field);
}

function isNumericValue(value) {
  if (value === null || value === undefined || value === "") return false;
  return !isNaN(parseFloat(value)) && isFinite(value);
}

function detectFieldType(data, field, maxCategories = MAX_CATEGORIES) {
  const rawValues = data
    .map((d) => d[field])
    .filter((v) => v !== null && v !== undefined && v !== "");

  if (rawValues.length === 0) return null;

  const numericRatio = rawValues.filter(isNumericValue).length / rawValues.length;
  const isMostlyNumeric = numericRatio > 0.9;

  const normalized = rawValues.map((v) => (isMostlyNumeric ? Number(v) : String(v).trim()));
  const uniqueValues = [...new Set(normalized)];

  const looksLikeFreeText =
    uniqueValues.length > maxCategories && uniqueValues.length / rawValues.length > 0.5;

  if (looksLikeFreeText) return { type: "text", uniqueCount: uniqueValues.length };

  if (isMostlyNumeric) {
    if (uniqueValues.length <= maxCategories) {
      return { type: "categorical", values: uniqueValues.sort((a, b) => a - b) };
    }
    return { type: "numeric", min: Math.min(...uniqueValues), max: Math.max(...uniqueValues) };
  }

  if (uniqueValues.length <= maxCategories) {
    return { type: "categorical", values: uniqueValues.sort() };
  }

  return { type: "text", uniqueCount: uniqueValues.length };
}

export function getLayerSchema(data, extraIgnoreFields = [], maxCategories = MAX_CATEGORIES) {
  if (!data || !data.length) return [];

  const fields = Object.keys(data[0]).filter((f) => !shouldIgnoreField(f, extraIgnoreFields));

  return fields
    .map((field) => {
      const info = detectFieldType(data, field, maxCategories);
      if (!info || info.type === "text") return null;
      return { field, ...info };
    })
    .filter(Boolean);
}

// ---------------------------------------------------------------------
// Color assignment — cartographic rules applied here
// ---------------------------------------------------------------------

// Okabe-Ito colorblind-safe qualitative palette, in the order recommended
// by Okabe & Ito (2008) — engineered so any prefix of length k stays
// maximally distinct, which is exactly what we need for small category
// counts (<10).
const ORDERED_PALETTE = [
  "#0072B2", // blue
  "#009E73", // bluish green / teal
  "#CC79A7", // reddish purple
  "#56B4E9", // sky blue
  "#000000", // black
  "#8B4513", // saddle brown
  "#8A2BE2", // blue-violet
  "#4B0082", // indigo
  "#2F4F4F", // dark slate gray
  "#DB7093", // pale violet red
];

// Conventional utility-status colors — encodes meaning consistently
// (Active is always this green everywhere in the system, not whatever
// hue happens to fall next in a sorted list).
const SEMANTIC_COLORS = [
  // Positive / normal-operating states — deep sky blue family
  { pattern: /^(active|operational|existing|ok|functional)$/i, color: "#000dff" }, // deep sky blue
  { pattern: /^resolved$/i, color: "#000dff" }, // steel blue

  // Negative states — red family, each shade distinct but all "problem"-coded
  { pattern: /^(inactive|not operational|dormant|closed)$/i, color: "#B22222" }, // firebrick
  { pattern: /^(damaged|faulty|broken|leak(ing)?|critical)$/i, color: "#8B0000" }, // dark red
  { pattern: /^(disconnected|abandoned)$/i, color: "#800000" }, // maroon
  { pattern: /^suspended$/i, color: "#DC143C" }, // crimson
  { pattern: /^reported$/i, color: "#A52A2A" }, // brown-red

  // Neutral / planned states — kept out of both the blue and red families
  { pattern: /^(proposed|planned|under enrollment|pending)$/i, color: "#00a55d" }, // slate blue

  { pattern: /^(n\/a|na|unknown|null)$/i, color: "#808080" }, // gray

  { pattern: /^(yes|replaced|complete|completed|done)$/i, color: "#000dff" }, // deep sky blue — resolved/done
  { pattern: /^(no|pending|not replaced)$/i, color: "#DC143C" }, // crimson — still outstanding
];

function getSemanticColor(label) {
  const match = SEMANTIC_COLORS.find((s) => s.pattern.test(String(label).trim()));
  return match ? match.color : null;
}

function hexToRgb(hex) {
  const clean = hex.replace("#", "");
  const bigint = parseInt(clean, 16);
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
}

function colorDistance(hex1, hex2) {
  const c1 = hexToRgb(hex1);
  const c2 = hexToRgb(hex2);
  const rMean = (c1.r + c2.r) / 2;
  const dr = c1.r - c2.r, dg = c1.g - c2.g, db = c1.b - c2.b;
  return Math.sqrt((2 + rMean / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rMean) / 256) * db * db);
}

const MIN_DISTINCT_DISTANCE = 90; // empirically distinguishable on a lit screen at icon size

// Golden-angle hue rotation for the rare case we exceed the curated
// palette. Skips the yellow/orange hue band (~25°–70°) so the low-contrast
// rule holds even for the fallback path.
function goldenAngleColor(index) {
  let hue = (index * 137.508) % 360;
  if (hue >= 25 && hue <= 70) hue += 120;
  return `hsl(${hue % 360}, 65%, 40%)`;
}

/**
 * Assigns one color per value: semantic keywords first (so "Active" is
 * always green, "Damaged" always vermillion/red, etc.), then the
 * remaining values get the next palette color that's perceptually
 * distinct from everything already assigned in this legend.
 */
function assignDistinctColors(values) {
  const assigned = {};
  const usedColors = [];

  values.forEach((v) => {
    const semantic = getSemanticColor(v);
    if (semantic && !usedColors.includes(semantic)) {
      assigned[v] = semantic;
      usedColors.push(semantic);
    }
  });

  const remaining = values.filter((v) => assigned[v] === undefined);
  let paletteIdx = 0;

  remaining.forEach((v) => {
    let chosen = null;
    let attempts = 0;

    while (attempts < ORDERED_PALETTE.length * 2) {
      const candidate = ORDERED_PALETTE[paletteIdx % ORDERED_PALETTE.length];
      paletteIdx++;
      attempts++;
      if (usedColors.every((u) => colorDistance(u, candidate) > MIN_DISTINCT_DISTANCE)) {
        chosen = candidate;
        break;
      }
    }

    if (!chosen) chosen = goldenAngleColor(usedColors.length);

    assigned[v] = chosen;
    usedColors.push(chosen);
  });

  return assigned;
}

function getSequentialRamp(steps) {
  const ramp = [];
  for (let i = 0; i < steps; i++) {
    const lightness = 80 - i * (55 / Math.max(steps - 1, 1));
    ramp.push(`hsl(212, 80%, ${lightness}%)`);
  }
  return ramp;
}

function getQuantileBreaks(sortedValues, buckets) {
  const breaks = [];
  const n = sortedValues.length;
  for (let i = 0; i < buckets; i++) {
    const lowIdx = Math.floor((i / buckets) * n);
    const highIdx = i === buckets - 1 ? n - 1 : Math.floor(((i + 1) / buckets) * n) - 1;
    breaks.push({ min: sortedValues[lowIdx], max: sortedValues[Math.max(highIdx, lowIdx)] });
  }
  return breaks;
}

export function buildClassification(data, field, fieldInfo, numericBuckets = 5) {
  if (!fieldInfo) return [];

  if (fieldInfo.type === "categorical") {
    const colors = assignDistinctColors(fieldInfo.values.map(String));
    return fieldInfo.values.map((value) => ({
      key: String(value),
      label: String(value),
      color: colors[String(value)],
      test: (val) => String(val).trim() === String(value),
    }));
  }

  if (fieldInfo.type === "numeric") {
    const sorted = data
      .map((d) => Number(d[field]))
      .filter((v) => !isNaN(v))
      .sort((a, b) => a - b);

    if (!sorted.length) return [];

    const buckets = Math.min(numericBuckets, new Set(sorted).size) || 1;
    const breaks = getQuantileBreaks(sorted, buckets);
    const ramp = getSequentialRamp(buckets);

    return breaks.map((range, i) => ({
      key: `range_${i}`,
      label: `${range.min.toFixed(1)} – ${range.max.toFixed(1)}`,
      color: ramp[i],
      test: (val) => {
        const n = Number(val);
        if (isNaN(n)) return false;
        return i === breaks.length - 1
          ? n >= range.min && n <= range.max
          : n >= range.min && n < range.max;
      },
    }));
  }

  return [];
}

export function getClassForValue(classes, value) {
  return classes.find((c) => c.test(value));
}

// Sentinel value for "don't categorize — just show every feature in one
// consistent color for this layer."
export const SHOW_ALL_KEY = "__show_all__";

function hashStringToIndex(str, mod) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) % 1000003;
  }
  return Math.abs(hash) % mod;
}

// Deterministic single color per layer name — same layer always gets the
// same color across reloads/sessions, and stays out of the yellow/orange
// band since it draws from the same ORDERED_PALETTE.
export function getLayerBaseColor(layerName) {
  return ORDERED_PALETTE[hashStringToIndex(layerName, ORDERED_PALETTE.length)];
}

// The single "class" used when a layer is set to show-all/uncategorized.
export function getShowAllClass(layerName) {
  return {
    key: SHOW_ALL_KEY,
    label: "All Features",
    color: getLayerBaseColor(layerName),
    test: () => true,
  };
}